import * as angular from 'angular';
import datafangstHelpers from "../../utilities";
import {Relevance} from "../representation/viewmodel";

export type ExtentType = "POINT" | "LINE" | "TURN";

export type AttributeRequirementType = "REQUIRED" | "RECOMMENDED" | "CONDITIONALLY_REQUIRED" | "OPTIONAL"
    | "LESS_IMPORTANT" | "NOT_IN_USE" | "UNDECIDED";

export type AttributeValueType = "NUMBER" | "STRING" | "ENUM" | "SPATIAL";

export type GeometryType = "POINT" | "LINE" | "POLYGON" | "MULTIPOINT" | "MULTILINE" | "MULTIPOLYGON";

export interface EnumerationType {
    id: number;
    value: string;
    inObjectList: boolean;
    sortNum: number;
}

export type SensitiveType = "0" | "1" | "2" | "3";

export interface AttributeType {
  id: number;
  name: string;
  description: string;
  sortNum: number;
  inObjectList: boolean;
  unit?: string;
  fieldLength?: number;
  numDecimals?: number;
  requirement: AttributeRequirementType;
  requirementComment?: string;
  requirementDisplayName: string;
  importance: number;
  valueType: AttributeValueType;
  enumerations: { [key: string]: EnumerationType };
  sensitiveType: SensitiveType;
  heightReference: number;
}

export interface NvdbFeatureType {
    id: number;
    name: string;
    description: string;
    sortNum: number;
    categories: number;
    inObjectList: boolean;
    extentType: ExtentType;
    parentNeeded: boolean;
    attributeTypes: { [key: string]: AttributeType };
    geometryTypes: GeometryType[];
    sidePositionRelevance: Relevance;
    laneRelevance: Relevance;
}

export interface FkbFeatureType {
    id: number;
    name: string;
    sortNum: number;
    nvdbId?: number;
    verified: boolean;
}

export interface AssociationType {
    id: number;
    parentFeatureTypeId: number;
    childFeatureTypeId: number;
}

export interface ObjectListItem {
    name: string;
    id: number;
    foreignId?: number;
}

export interface DataCatalogVersions {
    NVDB: string;
    NVDBID: string;
    FKB: string;
}

export type FeatureTypeIndex = { [key: string]: NvdbFeatureType };

export type FkbFeatureTypeIndex = { [key: string]: FkbFeatureType };

export type AssociationTypeIndex = { [key: string]: AssociationType };

export type ObjectListIndex = { [key: string]: ObjectListItem };

export default class datacatalog {
    public dataCatalogVersions: DataCatalogVersions = undefined;
    private featureTypes: FeatureTypeIndex = {};
    private fkbFeatureTypes: FkbFeatureTypeIndex = {};
    private associationTypes: AssociationTypeIndex = {};
    public roadCategories: { [key: string]: string } = undefined;
    public roadStatuses: { [key: string]: string } = undefined;
    private globalFkbObjectList: ObjectListIndex = {};
    private nvdbToFkbObjectListMap: Map<number, number> = undefined;

    constructor() {
        this.dataCatalogVersions = undefined;
        this.featureTypes = {};
        this.fkbFeatureTypes = {};
        this.associationTypes = {};
        this.roadCategories = undefined;
        this.roadStatuses = undefined;
        this.globalFkbObjectList = {};
        this.nvdbToFkbObjectListMap = undefined;
    }

    init(dataCatVersions: DataCatalogVersions, nvdbFeatureTypes: FeatureTypeIndex, fkbtypes: FkbFeatureTypeIndex,
         assocTypes: AssociationTypeIndex, fkb_objectlist: ObjectListIndex): void {
        this.dataCatalogVersions = dataCatVersions;
        this.featureTypes     = nvdbFeatureTypes;
        this.fkbFeatureTypes     = fkbtypes;
        this.associationTypes = assocTypes;
        this.globalFkbObjectList = fkb_objectlist;
        this.nvdbToFkbObjectListMap = this.buildNvdbToFkbObjectlistMap();
    }

    private static createRoadCategories(): { [key: string]: string } {
        return {
            E: 'Europaveg',
            R: 'Riksveg',
            F: 'Fylkesveg',
            K: 'Kommunal veg',
            P: 'Privat veg',
            S: 'Skogsbilveg'
        };
    }

    private static createRoadStatuses(): { [key: string]: string } {
        return {
            V: 'Eksisterende veg',
            W: 'Midlertidig veg',
            T: 'Midlertidig status bilveg',
            S: 'Eksisterende ferjestrekning',
            G: 'Gang-/sykkelveg',
            U: 'Midlertidig status gang-/sykkelveg',
            B: 'Beredskapsveg',
            M: 'Serviceveg',
            X: 'Rømningstunnel',
            A: 'Anleggsveg',
            H: 'Gang-/sykkelveg anlegg',
            P: 'Vedtatt veg',
            E: 'Vedtatt ferjestrekning',
            Q: 'Vedtatt gang-/sykkelveg'
        };
    }

    getFeatureTypeName(typeId: number, destination: 'NVDB' | 'FKB' = 'NVDB'): string {
        const featureTypes = destination == 'NVDB' ? this.featureTypes : this.fkbFeatureTypes;
        if (featureTypes[typeId]) {
            return featureTypes[typeId].name;
        } else {
            return `${destination} type ${typeId}`;
        }
    }

    getFeatureTypeSortNum(typeId: number): number {
        if (this.featureTypes[typeId]) {
            return this.featureTypes[typeId].sortNum;
        } else {
            return typeId;
        }
    }

    getFeatureTypes(): NvdbFeatureType[] {
        return datafangstHelpers.values(this.featureTypes);
    }

    getFKBFeatureTypes(): FkbFeatureType[] {
        return datafangstHelpers.values(this.fkbFeatureTypes);
    }

    getFeatureType(featureTypeId: number): NvdbFeatureType {
        return this.featureTypes[featureTypeId];
    }

    getFkbFeatureType(featureTypeId: number): FkbFeatureType {
        return this.fkbFeatureTypes[featureTypeId];
    }

    getFkbFeatureTypeNames(): string[] {
        return datafangstHelpers.values(this.fkbFeatureTypes)
            .map(featuretype => featuretype.name);
    }

    getFeatureTypeWithDestination(featureTypeId: number, destination: string) : NvdbFeatureType | FkbFeatureType {
        if (destination === 'NVDB'){
            return this.getFeatureType(featureTypeId);
        } else if (destination === 'FKB'){
            return this.getFkbFeatureType(featureTypeId);
        } else {
            throw new Error('Unknown destination: ' + destination);
        }
    }

    getAssociationTypesByParent(parentFeatureTypeId: number): AssociationType[] {
        return datafangstHelpers.values(this.associationTypes)
            .filter(assocType => assocType.parentFeatureTypeId == parentFeatureTypeId);
    }

    getAssociationTypesByChild(childFeatureTypeId: number): AssociationType[] {
        return datafangstHelpers.values(this.associationTypes)
            .filter(assocType => assocType.childFeatureTypeId == childFeatureTypeId);
    }

    getRoadCategories(): { [key: string]: string } {
        if (!this.roadCategories) {
            this.roadCategories = datacatalog.createRoadCategories();
        }
        return this.roadCategories;
    }

    getRoadStatuses(): { [key: string]: string } {
        if (!this.roadStatuses) {
            this.roadStatuses = datacatalog.createRoadStatuses();
        }
        return this.roadStatuses;
    }

    buildNvdbToFkbObjectlistMap(): Map<number, number> {
        const nvdbToFkbObjectListMap = new Map<number, number>();
        angular.forEach(this.globalFkbObjectList, function(fkbObject){
            if (fkbObject.foreignId !== null && fkbObject.foreignId >= 0){
                nvdbToFkbObjectListMap.set(fkbObject.foreignId, fkbObject.id);
            }
        });
        return nvdbToFkbObjectListMap;
    }

    // XXX nvdbObjectlist and fkbObjectlist from contract, no typings for this yet
    purgeStaleFkbObjects(nvdbObjectlist: any[], fkbObjectlist: any[]): any[] {
        const fkbMap = this.nvdbToFkbObjectListMap;
        return fkbObjectlist.filter(fkbObject => {
            const nvdbId = this.getKeyByValue(fkbMap, fkbObject.typeId);
            const isOnlyFkbType = !nvdbId;
            return isOnlyFkbType || nvdbObjectlist.some(nvdbObject => {
                return nvdbObject.typeId == nvdbId;
            });
        });
    }

    getKeyByValue(numberMap: Map<number, number>, value: number): number | undefined {
        for (const entry of numberMap.entries()) {
            if (entry[1] === value) {
                return entry[0];
            }
        }

        return undefined;
    }
}
datacatalog.$inject = ['$http', '$q'];
